import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldHeader } from '@/components/atoms/FieldHeader/FieldHeader';
import { ConditionField } from '@/components/organisms/ConditionField/ConditionField';
import { FormField } from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { ActionType } from '@/shared/types/rules';

import { AiAgentDraftHelp } from './AiAgentDraftHelp';
import { RuleFormValues } from './ruleFormSchema';
import { ActionsField } from '../ActionsField/ActionsField';
import { TriggersField } from '../TriggersField/TriggersField';

type RuleLogicFieldsProps = { canWrite: boolean; isCreate: boolean };

export const RuleLogicFields = ({ canWrite, isCreate }: RuleLogicFieldsProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<RuleFormValues>();
  const { errors, isSubmitted } = useFormState({ control });
  const actions = useWatch({ control, name: 'actions' });
  const assignedBrainId = actions.find(
    (action) => action.type === ActionType.ASSIGN_BRAIN && action.brain_parent_id
  )?.brain_parent_id;

  return (
    <div className="flex flex-col gap-6 pb-1">
      <FormField
        control={control}
        name="triggers"
        render={({ field }) => (
          <TriggersField
            value={field.value}
            onChange={field.onChange}
            disabled={!canWrite}
            defaultOpen={isCreate}
            error={errors.triggers?.message}
          />
        )}
      />
      <Separator />
      <div className="flex flex-col gap-4">
        <FieldHeader
          title={t('rules.editor.conditions_title')}
          description={t('rules.editor.conditions_description')}
          error={isSubmitted ? errors.condition?.message : undefined}
        />
        <FormField
          control={control}
          name="condition"
          render={({ field }) => (
            <ConditionField
              value={field.value}
              onChange={field.onChange}
              disabled={!canWrite}
              showErrors={isSubmitted}
            />
          )}
        />
      </div>
      <Separator />
      <FormField
        control={control}
        name="actions"
        render={({ field }) => (
          <ActionsField
            value={field.value}
            onChange={field.onChange}
            disabled={!canWrite}
            error={isSubmitted ? errors.actions?.message : undefined}
            showErrors={isSubmitted}
          />
        )}
      />
      {assignedBrainId && (
        <AiAgentDraftHelp key={assignedBrainId} brainParentId={assignedBrainId} />
      )}
    </div>
  );
};
